import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { ParsedQs } from "qs";
import { useFormContext } from "react-hook-form";

import AvatarPreview from "components/avatarPreview/AvatarPreview";
import { AvatarPreviewSlice } from "prismicio-types";
import {
  useGetCustomisation,
  useSetCustomisation,
} from "queries/product-queries";
import { useFindByKey } from "utils/schema-utils";

import styles from "./AvatarPreview.module.scss";

/**
 * Displays a preview image of the current Avatar.
 *
 * Uses a combination of active form values and 'presets' from the schema.
 *
 * Must be within a `FormProvider` to access the form data.
 */
export default function AvatarPreviewField({
  slice,
}: SliceComponentProps<AvatarPreviewSlice>) {
  const { watch } = useFormContext();

  const { data: customisation } = useGetCustomisation();
  const { mutate: setCustomisation } = useSetCustomisation();

  const findByKey = useFindByKey();

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const field = findByKey(schemaKey);

  if (!field) {
    return null;
  }

  if (!("characterTypeIndex" in field) || !("characterIndex" in field)) {
    throw new Error(
      `Avatar role "${
        schemaKey.split(".")[1] || schemaKey
      }" does not exist in the schema`
    );
  }

  // Fields from the form, watched for updates
  const customisations = watch(field.urlKey);

  const characterTypeData = _get(
    customisation,
    `characters.${field.characterTypeIndex}`
  );

  /**
   * If true, we're in an avatar type (e.g. "child") where we have too many customisations compared to the `count` the schema is expecting.
   *
   * On initial load, we create default values (e.g. `{ extras: none }`) for the maximum possible characters.
   *
   * When the user reduces this to e.g. 1 child, we need to unregister those superfluous entries or the book may present with additional characters named "undefined".
   *
   * We do this in here because it's super avatar-specific and would be a real turd anywhere else.
   */
  if (
    customisation &&
    characterTypeData &&
    typeof characterTypeData === "object" &&
    "count" in characterTypeData &&
    typeof characterTypeData.count === "string" &&
    "customisations" in characterTypeData &&
    Array.isArray(characterTypeData.customisations) &&
    Number(characterTypeData.count) < characterTypeData.customisations.length
  ) {
    const count = Number(characterTypeData.count);

    // A cloned customisation we'll trim the characters from
    const trimmedCustomisation = { ...customisation } as {
      characters: { customisations: ParsedQs[] }[];
    };
    // ^ Stubbed type where we know the relevant rough shape of this object

    trimmedCustomisation.characters[
      field.characterTypeIndex
    ].customisations.splice(count - characterTypeData.customisations.length);
    // ^ Clip the necessary number of elements off the end of the array

    setCustomisation({
      data: trimmedCustomisation,
      behaviour: "replace",
    });
  }

  return (
    <div className={styles.container}>
      <AvatarPreview customisations={customisations} schemaKey={schemaKey} />
    </div>
  );
}
